import { useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { api } from "../services/api/api";
import toast from "react-hot-toast";
import { t } from "i18next";

// Type pour les éléments multimédias (photos et signatures)
type MediaItem =
  | EasyTech.Photo
  | EasyTech.WorkorderPhoto
  | EasyTech.WorkorderSignature;

interface UseMediaExportOptions {
  workorderId: string;
  photos: MediaItem[];
  signatures: MediaItem[];
  getPhotoDownloadUrl: (photoId: string) => string;
  getSignatureDownloadUrl: (signatureId: string) => string;
}

export const useMediaExport = ({
  workorderId,
  photos,
  signatures,
  getPhotoDownloadUrl,
  getSignatureDownloadUrl,
}: UseMediaExportOptions) => {
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const exportMediaAsZip = async () => {
    setExporting(true);
    setProgress(0);
    setError(null);

    try {
      const zip = new JSZip();

      // Créer la structure de dossiers
      const photosFolder = zip.folder("photos");
      const photosBefore = photosFolder?.folder("before");
      const photosAfter = photosFolder?.folder("after");

      const signaturesFolder = zip.folder("signatures");
      const signaturesBefore = signaturesFolder?.folder("before");
      const signaturesAfter = signaturesFolder?.folder("after");

      const totalMedia = photos.length + signatures.length;
      let processedMedia = 0;

      // Fonction pour mettre à jour la progression
      const updateProgress = () => {
        processedMedia++;
        setProgress(Math.floor((processedMedia / totalMedia) * 100));
      };

      // Traiter les photos
      const photoPromises = photos.map(async (photo) => {
        try {
          const folderToUse =
            photo.type === "BEFORE" ? photosBefore : photosAfter;
          if (!folderToUse) throw new Error("Dossier non disponible");

          const url = getPhotoDownloadUrl(photo.id);
          const response = await api.getPhotos(url);

          // Détecter le type MIME de l'image depuis les headers ou le path
          let mimeType = "image/jpeg"; // Valeur par défaut
          let fileExtension = "jpg";

          if (response && typeof response === "object" && "headers" in response) {
            const contentType = (response as any).headers?.["content-type"] ||
                              (response as any).headers?.["Content-Type"];
            if (contentType && contentType.includes("image/")) {
              mimeType = contentType;
              if (contentType.includes("png")) fileExtension = "png";
              else if (contentType.includes("webp")) fileExtension = "webp";
              else if (contentType.includes("gif")) fileExtension = "gif";
            }
          }

          // Utiliser l'extension du path original si disponible
          if (photo.path) {
            const originalExt = photo.path.split(".").pop()?.toLowerCase();
            if (originalExt && ["png", "jpg", "jpeg", "webp", "gif"].includes(originalExt)) {
              fileExtension = originalExt === "jpg" ? "jpeg" : originalExt;
              mimeType = `image/${fileExtension}`;
            }
          }

          const blob = new Blob([response as ArrayBuffer], { type: mimeType });
          folderToUse.file(`${photo.id}.${fileExtension}`, blob);
          updateProgress();
        } catch (err) {
          console.error(
            `Erreur lors du traitement de la photo ${photo.id}:`,
            err
          );
        }
      });

      // Traiter les signatures
      const signaturePromises = signatures.map(async (signature) => {
        try {
          const folderToUse =
            signature.type === "BEFORE" ? signaturesBefore : signaturesAfter;
          if (!folderToUse) throw new Error("Dossier non disponible");

          const url = getSignatureDownloadUrl(signature.id);
          const response = await api.getPhotos(url);
          const blob = new Blob([response as ArrayBuffer], {
            type: "image/jpeg",
          });
          folderToUse.file(`${signature.id}.jpg`, blob);
          updateProgress();
        } catch (err) {
          console.error(
            `Erreur lors du traitement de la signature ${signature.id}:`,
            err
          );
        }
      });

      // Attendre que tous les fichiers soient traités
      await Promise.all([...photoPromises, ...signaturePromises]);

      // Générer le ZIP
      const zipBlob = await zip.generateAsync(
        {
          type: "blob",
          compression: "DEFLATE",
          compressionOptions: { level: 6 },
        },
        (metadata) => {
          if (metadata.percent) {
            setProgress(Math.floor(metadata.percent));
          }
        }
      );

      // Télécharger le ZIP
      saveAs(zipBlob, `workorder-${workorderId}-media.zip`);
      setProgress(100);
    } catch (err) {
      toast.error(t("workorder:files:error:exportMedia"));
      setError(
        err instanceof Error ? err.message : t("workorder:files:error:exportMedia")
      );
    } finally {
      setExporting(false);
    }
  };

  return {
    exportMediaAsZip,
    exporting,
    progress,
    error,
  };
};
